/**
 * Type-safe enum builder with strict key=value enforcement
 * Provides all variants needed for different use cases while maintaining type safety
 */

import { i18n, type MessageDescriptor } from '@lingui/core'

export type StrictEnum<T extends string> = { readonly [K in T]: K }

export function createStrictEnum<const T extends readonly string[]>(values: T): StrictEnum<T[number]> {
  const result: Record<string, string> = {}
  for (const value of values) {
    result[value] = value
  }
  return result as StrictEnum<T[number]>
}

/** A parser returns one of T, possibly nullable, possibly Promise-wrapped. */
export type EnumParser<T extends string> = (...args: never[]) => T | null | undefined | Promise<T | null | undefined>

export type EnumParserMap<T extends string> = Record<string, EnumParser<T>>

/** Helpers attached to every defineEnum result under the `$` namespace. */
export type EnumHelpers<T extends string, TParsers extends EnumParserMap<T> = Record<string, never>> = {
  readonly enum: StrictEnum<T>
  Type: T
  readonly values: ReadonlyArray<T>
  readonly keys: ReadonlyArray<T>
  is(value: unknown): value is T
  zodEnum(): readonly [T, ...T[]]
  readonly messages: Readonly<Record<T, MessageDescriptor>>
  readonly valueEnum: { readonly [K in T]: { text: string } }
  getLabel<V extends T | null | undefined>(
    value: V,
    values?: Record<string, unknown>,
  ): V extends null | undefined ? undefined : string
  options: Array<{ label: string; value: T }>
  items: Array<{ type: 'item'; label: string; key: T }>
  /** Overlapping keys are overridden by extras. */
  extend<const E extends Record<string, MessageDescriptor>>(
    extras: E,
  ): EnumDefinition<StrictEnum<T | Extract<keyof E, string>>, TParsers>
  /** Parser map. Empty `{}` on enums that never called `.withParsers`. */
  readonly parse: Readonly<TParsers>
  /** Attach named parsers; merges with any previously attached parsers. */
  withParsers<const P extends EnumParserMap<T>>(parsers: P): EnumDefinition<StrictEnum<T>, Omit<TParsers, keyof P> & P>
}

export type EnumDefinition<
  TEnum extends StrictEnum<string>,
  TParsers extends EnumParserMap<TEnum[keyof TEnum]> = Record<string, never>,
> = TEnum & {
  readonly $: EnumHelpers<TEnum[keyof TEnum], TParsers>
}

/** Input shape for the rich overload: each value carries a `label` plus any extra metadata */
type RichMessageConfig = Record<string, { label: MessageDescriptor } & Record<string, unknown>>

/** Additional members attached to `$` when extras are provided via the rich overload */
export type RichEnumHelpers<
  TKey extends string,
  T extends Record<TKey, { label: MessageDescriptor }>,
  TParsers extends EnumParserMap<TKey> = Record<string, never>,
> = {
  /** Returns the extras object (everything except `label`) for a given enum value */
  getMeta<K extends TKey>(key: K): Omit<T[K], 'label'>
  /** Map of all extras keyed by enum value */
  readonly meta: { readonly [K in TKey]: Omit<T[K], 'label'> }
  /** Lazily evaluated and cached on first access. */
  readonly richOptions: Array<{ type: TKey; label: string } & Omit<T[TKey], 'label'>>
  /** Returns a new rich enum with the extension's entries added; overlapping keys are overridden. */
  extend<const E extends RichMessageConfig>(
    extras: E,
  ): EnumDefinition<StrictEnum<TKey | Extract<keyof E, string>>, TParsers> & {
    readonly $: EnumHelpers<TKey | Extract<keyof E, string>, TParsers> &
      RichEnumHelpers<TKey | Extract<keyof E, string>, Omit<T, keyof E> & E, TParsers>
  }
}

// Overload 1: rich form — values are { label: MessageDescriptor, ...extras }
export function defineEnum<const T extends RichMessageConfig>(
  config: T,
): EnumDefinition<StrictEnum<Extract<keyof T, string>>, Record<string, never>> & {
  readonly $: EnumHelpers<Extract<keyof T, string>> & RichEnumHelpers<Extract<keyof T, string>, T>
}
// Overload 2: plain form — values are MessageDescriptor (existing behaviour, unchanged)
export function defineEnum<const T extends Record<string, MessageDescriptor>>(
  messages: T,
): EnumDefinition<StrictEnum<Extract<keyof T, string>>>
export function defineEnum(input: Record<string, unknown>): unknown {
  type EnumKey = string

  // Detect rich vs plain by checking whether the first value has a `label` object property.
  // MessageDescriptor values produced by msg`...` never have a `label` key.
  const firstValue = Object.values(input)[0]
  const isRich =
    firstValue !== null &&
    typeof firstValue === 'object' &&
    'label' in (firstValue as object) &&
    typeof (firstValue as { label: unknown }).label === 'object'

  let messages: Record<string, MessageDescriptor>
  let extrasMap: Record<string, Record<string, unknown>> | undefined

  if (isRich) {
    messages = {}
    extrasMap = {}
    for (const [key, value] of Object.entries(
      input as Record<string, { label: MessageDescriptor } & Record<string, unknown>>,
    )) {
      const { label, ...extras } = value
      messages[key] = label
      extrasMap[key] = extras
    }
  } else {
    messages = input as Record<string, MessageDescriptor>
  }

  const translateMessage = (message: MessageDescriptor, values?: Record<string, unknown>): string => {
    const id = message.id ?? message.message
    if (!id) {
      return ''
    }
    return i18n._(id, values, { message: message.message })
  }

  const keys = Object.keys(messages) as EnumKey[]
  const enumObject: Record<string, string> = {}
  for (const key of keys) {
    enumObject[key] = key
  }

  const valuesArray = [...keys] as ReadonlyArray<EnumKey>
  const keysArray = [...keys] as ReadonlyArray<EnumKey>

  const getLabel = <V extends EnumKey | null | undefined>(
    value: V,
    values?: Record<string, unknown>,
  ): V extends null | undefined ? undefined : string => {
    if (value === null || value === undefined) {
      return undefined as V extends null | undefined ? undefined : string
    }
    const message = messages[value as EnumKey]
    return (message ? translateMessage(message, values) : value) as V extends null | undefined ? undefined : string
  }

  const is = (value: unknown): value is EnumKey => {
    return typeof value === 'string' && keys.includes(value as EnumKey)
  }

  const zodEnum = (): readonly [EnumKey, ...EnumKey[]] => {
    if (valuesArray.length === 0) {
      throw new Error('Enum must have at least one value')
    }
    const first = valuesArray[0]
    if (!first) {
      throw new Error('First enum value is missing')
    }
    const rest = valuesArray.slice(1)
    return [first, ...rest] as const
  }

  const makeExtend = (currentParsers: Record<string, (...args: unknown[]) => unknown>) => {
    return (extras: Record<string, unknown>) => {
      const next = (defineEnum as (input: Record<string, unknown>) => unknown)({
        ...input,
        ...extras,
      }) as { $: { withParsers: (p: Record<string, unknown>) => unknown } }
      return Object.keys(currentParsers).length === 0 ? next : next.$.withParsers(currentParsers)
    }
  }

  const makeWithParsers = (currentParsers: Record<string, (...args: unknown[]) => unknown>) => {
    return (extras: Record<string, (...args: unknown[]) => unknown>) => {
      const nextParsers = { ...currentParsers, ...extras }
      return {
        ...enumObject,
        $: {
          ...helpers,
          parse: nextParsers,
          withParsers: makeWithParsers(nextParsers),
          extend: makeExtend(nextParsers),
        },
      }
    }
  }

  const helpers: Record<string, unknown> = {
    enum: enumObject as StrictEnum<EnumKey>,
    Type: undefined as unknown as EnumKey,
    values: valuesArray,
    keys: keysArray,
    is,
    zodEnum,
    messages: messages as Readonly<Record<EnumKey, MessageDescriptor>>,
    getLabel,
    extend: makeExtend({}),
    parse: {} as Record<string, (...args: unknown[]) => unknown>,
    withParsers: makeWithParsers({}),
  }

  let cachedValueEnum: { readonly [K in EnumKey]: { text: string } } | undefined
  let cachedOptions: Array<{ label: string; value: EnumKey }> | undefined
  let cachedItems: Array<{ type: 'item'; label: string; key: EnumKey }> | undefined

  Object.defineProperty(helpers, 'valueEnum', {
    get() {
      if (!cachedValueEnum) {
        const valueEnum: Record<string, { text: string }> = {}
        for (const key of keys) {
          const message = messages[key]
          if (message) {
            valueEnum[key] = { text: translateMessage(message) }
          }
        }
        cachedValueEnum = valueEnum as { readonly [K in EnumKey]: { text: string } }
      }
      return cachedValueEnum
    },
    enumerable: true,
    configurable: true,
  })

  Object.defineProperty(helpers, 'options', {
    get() {
      if (!cachedOptions) {
        cachedOptions = valuesArray.map(value => ({ label: getLabel(value), value }))
      }
      return cachedOptions
    },
    enumerable: true,
    configurable: true,
  })

  Object.defineProperty(helpers, 'items', {
    get() {
      if (!cachedItems) {
        cachedItems = valuesArray.map(key => ({ type: 'item' as const, label: getLabel(key), key }))
      }
      return cachedItems
    },
    enumerable: true,
    configurable: true,
  })

  if (extrasMap) {
    const capturedExtrasMap = extrasMap
    helpers.getMeta = (key: string) => capturedExtrasMap[key]
    helpers.meta = capturedExtrasMap

    const translateExtras = (extras: Record<string, unknown>): Record<string, unknown> => {
      const out: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(extras)) {
        if (
          v !== null &&
          typeof v === 'object' &&
          'message' in (v as object) &&
          (('id' in (v as object) && typeof (v as { id: unknown }).id === 'string') ||
            typeof (v as { message: unknown }).message === 'string')
        ) {
          out[k] = translateMessage(v as MessageDescriptor)
        } else {
          out[k] = v
        }
      }
      return out
    }

    let cachedRichOptions: Array<{ type: string; label: string } & Record<string, unknown>> | undefined
    Object.defineProperty(helpers, 'richOptions', {
      get() {
        if (!cachedRichOptions) {
          cachedRichOptions = valuesArray.map(key => ({
            type: key,
            label: getLabel(key),
            ...translateExtras(capturedExtrasMap[key] ?? {}),
          }))
        }
        return cachedRichOptions
      },
      enumerable: true,
      configurable: true,
    })
  }

  return { ...enumObject, $: helpers }
}
